import React, { FC, useState, useCallback, useRef, useEffect, memo } from "react";
import { createPortal } from "react-dom";
import { createPopper } from "@popperjs/core";
import type { Instance as PopperInstance, VirtualElement } from "@popperjs/core";
import { Popconfirm } from "antd";
import { useAlert } from "../../hooks/useAlert";
import { AlertMessage } from "../../components/common/AlertMessage";
import { PolishLoadingSparkles } from "../../components/common/PolishLoadingSparkles";
import "./VoiceAi.scss";
import {
  voicePerformanceVoicesApi,
  voicePerformanceGenerateApi,
  voicePerformanceGenerateWithImageApi,
  voicePerformancePolishApi,
  voicePerformanceTasksApi,
  voicePerformanceTaskDeleteApi,
  voicePerformanceTemplatesApi,
} from "../../service/voicePerformance";
import type {
  VoiceProfileListResp,
  VoicePerformanceTaskItem,
  VoicePerformanceTaskPageResp,
  VoicePerformanceTemplate,
} from "../../service/voicePerformance";
import { request } from "../../utils/request";
import { mapTimbreDesignRowToVoiceToneItem } from "../../service/timbreDesign";
import { resolvePublicVoicePreviewFullUrl } from "../../service/voicePublic";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { triggerPointsRefresh } from "../../service/points";
import { triggerMembershipRefresh } from "../../service/membership";

const MAX_CHARS = 1500;
const MAX_VOICES = 3;
const TEMPLATE_PLAY_ID_OFFSET = 1_000_000;

function getTemplatePlaybackId(id: number): number {
  return -(TEMPLATE_PLAY_ID_OFFSET + id);
}

// 音色（公共 + 我的）
interface VoiceOption {
  id: number;
  name: string;
  description?: string;
  gender: string;
  genderType: "female" | "male" | "neutral";
  style: string;
  avatarUrl: string;
  previewAudioUrl?: string;
  source: "public" | "mine";
}

function resolvePreviewUrl(rawUrl?: string): string | undefined {
  if (!rawUrl) return undefined;
  if (rawUrl.startsWith("http://")) return `https://${rawUrl.slice("http://".length)}`;
  if (rawUrl.startsWith("https://")) return rawUrl;
  const path = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
  return `${VIDEO_CDN_PREFIX}${path}`;
}

function toVoiceOption(v: VoiceProfileListResp): VoiceOption {
  return {
    id: v.id,
    name: v.displayName,
    description: v.description || "",
    genderType: v.gender === "female" ? "female" : v.gender === "male" ? "male" : "neutral",
    gender: v.gender === "female" ? "女声" : v.gender === "male" ? "男声" : "中性",
    style: v.scene || "通用",
    avatarUrl: v.avatarUrl,
    previewAudioUrl: resolvePreviewUrl(v.previewAudioUrl),
    source: "public",
  };
}

function toMineVoiceOption(v: VoicePublic.VoiceToneItem): VoiceOption {
  const genderRaw = (v.genderTextValue || "").trim();
  let genderType: VoiceOption["genderType"] = "neutral";
  if (genderRaw.includes("女")) genderType = "female";
  else if (genderRaw.includes("男")) genderType = "male";
  return {
    id: v.id,
    name: v.voiceName || "",
    description: v.description || "",
    genderType,
    gender: genderRaw || "中性",
    style: "",
    avatarUrl: "",
    previewAudioUrl: resolvePublicVoicePreviewFullUrl(v, VIDEO_CDN_PREFIX) || undefined,
    source: "mine",
  };
}

/**
 * 获取「我的音色」选项
 * 接口：GET /video/timbre-design/page（音色库页面 /voice-library/public 使用的接口）
 * 响应数据结构：TimbreDesign.TimbreDesignPageResponse
 */
async function fetchMineVoiceOptionsApi(keyword: string): Promise<VoicePublic.VoiceToneItem[]> {
  try {
    const res = await request<TimbreDesign.TimbreDesignPageResponse>(
      "/video/timbre-design/page",
      {
        method: "GET",
        params: {
          pageNo: 1,
          pageSize: 100,
          ...(keyword ? { voiceName: keyword } : {}),
        },
      }
    );
    if (res.code === 0 && res.data && Array.isArray(res.data.list)) {
      return res.data.list.map(mapTimbreDesignRowToVoiceToneItem);
    }
    return [];
  } catch {
    return [];
  }
}

// 公共音色头像颜色池（与原始 html 一致）
const VOICE_COLORS = [
  "#ec4899", "#334155", "#2563eb", "#f97316",
  "#0f766e", "#7c3aed", "#0891b2", "#dc2626",
  "#ca8a04", "#059669", "#9333ea", "#db2777",
];

function getVoiceColor(id: number): string {
  return VOICE_COLORS[((id - 1) % VOICE_COLORS.length + VOICE_COLORS.length) % VOICE_COLORS.length];
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

const TOKEN_CLASSES = new Set(["male", "female", "neutral", "removed"]);

function genderClass(voice: VoiceOption): string {
  if (voice.genderType === "female") return "female";
  if (voice.genderType === "male") return "male";
  return "neutral";
}

function voiceSourceTypeOf(voice: VoiceOption): string {
  return voice.source === "mine" ? "USER_VOICE_TONE" : "PUBLIC_VOICE_TONE";
}

/** 一个音色 chip 计入的字数：@ + 音色名（无自动尾随空格） */
function getVoiceChipTextLength(voice: VoiceOption): number {
  return voice.name.length + 1;
}

function voiceSpanHtml(voiceId: number, tokenClass: string, name: string): string {
  const cls = TOKEN_CLASSES.has(tokenClass) ? tokenClass : "neutral";
  const display = name.replace(/^@/, "");
  return `<span contenteditable="false" voice="${voiceId}" class="${cls}">@${escapeHtml(display)}</span>`;
}

/** 将纯文本按换行插入光标处（contentEditable 用 <br> 表示换行） */
function insertPlainTextAtRange(range: Range, text: string): void {
  const lines = text.split("\n");
  range.deleteContents();
  lines.forEach((line, i) => {
    if (i > 0) {
      const br = document.createElement("br");
      range.insertNode(br);
      range.setStartAfter(br);
    }
    const node = document.createTextNode(line);
    range.insertNode(node);
    range.setStartAfter(node);
  });
  range.collapse(true);
}

/** 获取当前光标所在的视口坐标（不依赖 startContainer 类型）；原生 rect 失效时用临时 marker 兜底 */
function getCaretRect(): DOMRect | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  const rects = range.getClientRects();
  if (rects.length > 0 && rects[0].width > 0) return rects[0];
  const rect = range.getBoundingClientRect();
  if (rect && (rect.width > 0 || rect.height > 0)) return rect;

  // 兜底：collapsed range 在元素边界/部分浏览器会返回 0 矩形，插入临时 marker 精确定位
  const marker = document.createElement("span");
  marker.textContent = "\u200b";
  try {
    const cloneRange = range.cloneRange();
    cloneRange.insertNode(marker);
    const markerRect = marker.getBoundingClientRect();
    marker.remove();
    if (markerRect && (markerRect.width > 0 || markerRect.height > 0)) return markerRect;
    return null;
  } catch {
    return null;
  }
}

/**
 * 收集光标前的文本（同一条内联文本流内），用于 @ 提及检测。
 * 不依赖 startContainer 是否为 TEXT_NODE；兄弟节点遍历完后沿祖先上溯，
 * 遇到 chip / 块级（DIV/P）/ <br> 即停（TreeWalker 会跨块，故用手动反向遍历）。
 */
function getTextBeforeCaret(range: Range): string {
  const container = range.startContainer;
  const offset = range.startOffset;

  const isBoundary = (el: HTMLElement): boolean => {
    const tag = el.tagName.toUpperCase();
    return (
      (el.hasAttribute("contenteditable") && el.getAttribute("contenteditable") === "false") ||
      tag === "BR" || tag === "DIV" || tag === "P"
    );
  };

  const collectLeft = (node: Node | null): string => {
    let text = "";
    while (node) {
      if (node.nodeType === Node.TEXT_NODE) {
        text = (node as Text).data + text;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (isBoundary(el)) break;
        text = (el.textContent || "") + text;
      }
      node = node.previousSibling;
    }
    return text;
  };

  let text = "";
  let node: Node | null = null;
  if (container.nodeType === Node.TEXT_NODE) {
    text = (container as Text).data.slice(0, offset);
    node = container.previousSibling;
  } else if (container.nodeType === Node.ELEMENT_NODE) {
    const children = container.childNodes;
    const idx = Math.min(offset, children.length);
    node = idx > 0 ? children[idx - 1] : null;
  } else {
    return "";
  }

  text = collectLeft(node) + text;

  let guard = 0;
  let asc = container.parentElement;
  while (asc && guard++ < 50) {
    if (isBoundary(asc)) break;
    const left = collectLeft(asc.previousSibling);
    if (left) text = left + text;
    asc = asc.parentElement;
  }
  return text;
}

/** 定位光标前最后一个 @ 所在的文本节点与偏移（与 getTextBeforeCaret 同一套反向遍历），用于删除 @关键字 */
function findMentionAtPosition(range: Range): { node: Text; offset: number } | null {
  const container = range.startContainer;
  const offset = range.startOffset;

  const isBoundary = (el: HTMLElement): boolean => {
    const tag = el.tagName.toUpperCase();
    return (
      (el.hasAttribute("contenteditable") && el.getAttribute("contenteditable") === "false") ||
      tag === "BR" || tag === "DIV" || tag === "P"
    );
  };

  const innerTextNodes = (el: HTMLElement): Text[] => {
    const out: Text[] = [];
    const walk = (n: Node) => {
      if (n.nodeType === Node.TEXT_NODE) {
        out.push(n as Text);
      } else if (n.nodeType === Node.ELEMENT_NODE) {
        const e = n as HTMLElement;
        if (!isBoundary(e)) Array.from(e.childNodes).forEach(walk);
      }
    };
    Array.from(el.childNodes).forEach(walk);
    return out;
  };

  const collected: Text[] = [];
  let cursorText: Text | null = null;
  let startPrev: Node | null = null;

  if (container.nodeType === Node.TEXT_NODE) {
    cursorText = container as Text;
    collected.push(cursorText);
    startPrev = container.previousSibling;
  } else if (container.nodeType === Node.ELEMENT_NODE) {
    const children = container.childNodes;
    const idx = Math.min(offset, children.length);
    startPrev = idx > 0 ? children[idx - 1] : null;
  } else {
    return null;
  }

  const leftPart: Text[] = [];
  let node = startPrev;
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) {
      leftPart.unshift(node as Text);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (isBoundary(el)) break;
      const inner = innerTextNodes(el);
      leftPart.unshift(...inner);
    }
    node = node.previousSibling;
  }

  let guard = 0;
  let asc = container.parentElement;
  while (asc && guard++ < 50) {
    if (isBoundary(asc)) break;
    const prev = asc.previousSibling;
    if (prev) {
      const more: Text[] = [];
      let n: ChildNode | null = prev;
      while (n) {
        if (n.nodeType === Node.TEXT_NODE) {
          more.unshift(n as Text);
        } else if (n.nodeType === Node.ELEMENT_NODE) {
          const el = n as HTMLElement;
          if (isBoundary(el)) break;
          const inner = innerTextNodes(el);
          more.unshift(...inner);
        }
        n = n.previousSibling;
      }
      leftPart.unshift(...more);
    }
    asc = asc.parentElement;
  }

  collected.unshift(...leftPart);

  // 光标所在文本节点只取 offset 前缀；从后往前找最后一个 @
  const effectiveLen = (n: Text): number => (n === cursorText ? offset : n.data.length);
  for (let i = collected.length - 1; i >= 0; i--) {
    const n = collected[i];
    const at = n.data.lastIndexOf("@", effectiveLen(n) - 1);
    if (at !== -1) return { node: n, offset: at };
  }
  return null;
}

/** 从脚本中提取所有不重复的 @音色名 引用 */
function normalizeVoiceIds(rawVoiceIds?: number[] | string): number[] {
  if (Array.isArray(rawVoiceIds)) return rawVoiceIds;
  if (!rawVoiceIds) return [];
  try {
    const parsed = JSON.parse(rawVoiceIds);
    return Array.isArray(parsed) ? parsed.map(Number).filter(Number.isFinite) : [];
  } catch {
    return rawVoiceIds
      .split(",")
      .map((item) => Number(item.replace(/\D/g, "")))
      .filter(Number.isFinite);
  }
}
function formatHistoryTitle(html: string, allVoices: VoiceOption[], maxLength = 34): string {
  const displayText = plainScriptText(html, allVoices);
  return displayText.length > maxLength ? `${displayText.slice(0, maxLength)}...` : displayText;
}
function extractVoiceTagsFromHtml(html: string, allVoices: VoiceOption[]): string[] {
  const names = new Set<string>();
  const temp = document.createElement("div");
  temp.innerHTML = sanitizeScriptHtml(html, allVoices);
  temp.querySelectorAll("span[voice]").forEach((span) => {
    const id = Number(span.getAttribute("voice"));
    const v = allVoices.find((x) => x.id === id);
    names.add(v?.name || (span.textContent || "").replace(/^@/, "") || `音色${id}`);
  });
  return [...names];
}

/**
 * 将后端脚本 HTML 消毒为受控格式：
 * 只保留文本与 <span voice class contenteditable>（class 白名单），兼容旧 {{voice:id}}，<div>/<p>/<br> 规整为 \n
 */
function sanitizeScriptHtml(html: string, voices: VoiceOption[]): string {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const parts: string[] = [];
  const walk = (nodes: NodeListOf<ChildNode>) => {
    Array.from(nodes).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        const legacy = /\{\{voice:(\d+)\}\}/g;
        let m: RegExpExecArray | null;
        let lastIndex = 0;
        while ((m = legacy.exec(text)) !== null) {
          if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
          const id = Number(m[1]);
          const v = voices.find((x) => x.id === id);
          parts.push(voiceSpanHtml(id, v ? genderClass(v) : "neutral", v?.name || `音色${id}`));
          lastIndex = m.index + m[0].length;
        }
        if (lastIndex === 0) {
          parts.push(text);
        } else if (lastIndex < text.length) {
          parts.push(text.slice(lastIndex));
        }
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (tag === "br") {
        parts.push("\n");
        return;
      }
      if (tag === "div" || tag === "p") {
        walk(el.childNodes);
        parts.push("\n");
        return;
      }
      if (tag === "span") {
        const rawId = Number(el.getAttribute("voice"));
        if (Number.isFinite(rawId) && rawId > 0) {
          const cls = (el.getAttribute("class") || "").trim();
          parts.push(voiceSpanHtml(rawId, cls, (el.textContent || "").trim()));
          return;
        }
      }
      walk(el.childNodes);
    });
  };
  walk(doc.body.childNodes);
  return parts.join("");
}

/** 将编辑器 DOM 序列化为后端 HTML 脚本：removed span 丢弃、块级换行规整为 \n */
function serializeEditorScript(editor: HTMLElement): string {
  const parts: string[] = [];
  const walkChildren = (el: HTMLElement) => {
    Array.from(el.childNodes).forEach((node) => walk(node));
  };
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent || "");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === "br") {
      parts.push("\n");
      return;
    }
    if (tag === "div" || tag === "p") {
      walkChildren(el);
      parts.push("\n");
      return;
    }
    if (tag === "span") {
      const cls = (el.getAttribute("class") || "").trim();
      if (cls.split(/\s+/).includes("removed")) return;
      const rawId = Number(el.getAttribute("voice"));
      if (Number.isFinite(rawId) && rawId > 0) {
        parts.push(voiceSpanHtml(rawId, cls, (el.textContent || "").trim()));
      }
      return;
    }
    walkChildren(el);
  };
  walkChildren(editor);
  return parts.join("");
}

/**
 * 统一清洗：只处理不可见控制字符，不压缩空格、不 trim。
 * 因为空格、回车后端也计字符，前端计数需与之保持一致。
 */
function normalizePlain(s: string): string {
  return s.replace(/\u00A0/g, " ").replace(/[\u200B-\u200D\uFEFF]/g, "");
}

/**
 * 统一提取编辑器有效文本，用于字数统计：
 * - 普通文字 / 空格 / 换行全部计入（与后端字符模型一致，不压缩、不 trim）
 * - 正常音色 chip（contenteditable=false + voice 属性）计入其 textContent（@名字）
 * - removed 灰色 chip 属于失效状态：不计入
 * - 块级（DIV/P）与 <br> 作为行分隔（\n），行与行之间以一个 \n 连接；
 *   空编辑器仅剩占位 <br> 时结果为 ""（计 0）
 */
function getEditorPlainText(root: HTMLElement): string {
  const lines: string[] = [];
  let current = "";

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += node.textContent || "";
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    if (el.getAttribute("contenteditable") === "false") {
      if (el.hasAttribute("voice") && !el.classList.contains("removed")) {
        current += el.textContent || "";
      }
      return;
    }
    const tag = el.tagName.toUpperCase();
    if (tag === "BR") {
      // 仅当本行已有内容时才算换行；空行占位 <br> 由外层块边界处理
      if (current !== "") {
        lines.push(current);
        current = "";
      }
      return;
    }
    if (tag === "DIV" || tag === "P") {
      if (current !== "") {
        lines.push(current);
        current = "";
      }
      Array.from(el.childNodes).forEach(walk);
      lines.push(current);
      current = "";
      return;
    }
    Array.from(el.childNodes).forEach(walk);
  };

  Array.from(root.childNodes).forEach(walk);
  if (current !== "") lines.push(current);

  return normalizePlain(lines.join("\n"));
}

/** 计入 charCount 的字符数：统一走 getEditorPlainText（正常 chip 计入、removed 跳过） */
function countLiveChars(editor: HTMLElement): number {
  return getEditorPlainText(editor).length;
}

/** 提取纯文本（卡片标题 / 模板描述） */
function plainScriptText(html: string, voices: VoiceOption[]): string {
  const clean = sanitizeScriptHtml(html, voices);
  const temp = document.createElement("div");
  temp.innerHTML = clean;
  return (temp.textContent || "").replace(/\s+/g, " ").trim();
}

/** 把后端返回的 ISO 时间戳格式化为 YYYY-MM-DD HH:mm；空值/无效值返回空字符串 */
function formatHistoryTime(value: string | undefined | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${mo}-${da} ${hh}:${mi}`;
}

/** 渲染为带色 token 的 React 节点（历史/详情展示） */
function renderScriptHtml(html: string, voices: VoiceOption[]): React.ReactNode[] {
  const clean = sanitizeScriptHtml(html, voices);
  const temp = document.createElement("div");
  temp.innerHTML = clean;
  const nodes: React.ReactNode[] = [];
  let key = 0;
  const walk = (nodesList: NodeListOf<ChildNode>) => {
    Array.from(nodesList).forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        nodes.push(node.textContent || "");
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (tag === "br") {
        nodes.push("\n");
        return;
      }
      if (tag === "div" || tag === "p") {
        walk(el.childNodes);
        nodes.push("\n");
        return;
      }
      if (tag === "span" && el.hasAttribute("voice")) {
        const cls = (el.getAttribute("class") || "neutral").trim();
        const isRemoved = cls.split(/\s+/).includes("removed");
        nodes.push(
          <span
            key={key++}
            className={isRemoved ? "voice-ai-script-token removed" : `voice-ai-script-token ${cls}`}
          >
            {el.textContent}
          </span>
        );
        return;
      }
      walk(el.childNodes);
    });
  };
  walk(temp.childNodes);
  return nodes;
}
// ===== 独立滑块组件（本地 state，拖动不触发父组件重渲染） =====
const AudioSlider = memo<{
  label: string;
  min: number; max: number;
  value: number;
  onChange: (v: number) => void;
  formatValue: (v: number) => string;
}>(({ label, min, max, value, onChange, formatValue }) => {
  const [local, setLocal] = useState(value);
  const localRef = useRef(value);
  const lastCommitted = useRef(value);
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  if (value !== lastCommitted.current) {
    lastCommitted.current = value;
    localRef.current = value;
    setLocal(value);
  }

  const syncValue = useCallback((next: number) => {
    localRef.current = next;
    lastCommitted.current = next;
    setLocal(next);
    onChange(next);
  }, [onChange]);

  const pct = ((local - min) / (max - min)) * 100;

  const updateFromClient = useCallback((clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let ratio = (clientX - rect.left) / rect.width;
    ratio = Math.max(0, Math.min(1, ratio));
    const next = Math.round(min + ratio * (max - min));
    syncValue(next);
  }, [min, max, syncValue]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    updateFromClient(e.clientX);
    const onMove = (ev: MouseEvent) => { if (dragging.current) updateFromClient(ev.clientX); };
    const onUp = () => {
      dragging.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      syncValue(localRef.current);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [updateFromClient, syncValue]);

  return (
    <div className="voice-ai-slider-group">
      <label className="voice-ai-slider-label">{label}</label>
      <div className="voice-ai-slider" ref={trackRef} onMouseDown={handleMouseDown}>
        <div className="voice-ai-slider-fill" style={{ width: `${pct}%` }} />
        <div className="voice-ai-slider-knob" style={{ left: `${pct}%` }} />
      </div>
      <span className="voice-ai-slider-value">{formatValue(local)}</span>
    </div>
  );
});


interface HistoryItem {
  id: number;
  text: string;
  voices: string;
  duration: number;
  status: string;
  audioUrl?: string;
  errorMessage?: string;
  speechRate?: number;
  pitchRate?: number;
  loudnessRate?: number;
  referenceImageUrl?: string;
  /** 后端返回的创建时间（ISO 字符串），用于卡片底部展示 */
  createTime?: string;
}

const TERMINAL_TASK_STATUSES = new Set([
  "success",
  "failed",
  "fail",
  "error",
  "cancelled",
  "canceled",
  "completed",
]);

function normalizeTaskStatus(status?: string): string {
  return (status || "").trim().toLowerCase();
}

function isTerminalStatus(status?: string): boolean {
  return TERMINAL_TASK_STATUSES.has(normalizeTaskStatus(status));
}

function isUnfinishedStatus(status?: string): boolean {
  return !isTerminalStatus(status);
}

function isFailedStatus(status?: string): boolean {
  const normalized = normalizeTaskStatus(status);
  return normalized === "failed" || normalized === "fail" || normalized === "error";
}

function formatVoicesLabel(snapshot?: string): string {
  const raw = (snapshot || "").trim();
  if (!raw || raw === "[]" || raw === "null") return "";
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => {
          if (!item || typeof item !== "object") return "";
          const row = item as { displayName?: string; name?: string };
          return (row.displayName || row.name || "").trim();
        })
        .filter(Boolean)
        .join("、");
    }
  } catch {
    if (raw.startsWith("[")) return "";
  }
  return raw;
}

function mapTaskToHistoryItem(t: VoicePerformanceTaskItem): HistoryItem {
  return {
    id: t.id ?? t.taskId ?? 0,
    text: t.script ?? t.text ?? "",
    voices: formatVoicesLabel(t.voicesSnapshot),
    duration: t.duration ?? 0,
    status: t.status,
    audioUrl: resolvePreviewUrl(t.outputAudioUrl ?? t.audioUrl),
    errorMessage: t.errorMessage,
    speechRate: t.speechRate,
    pitchRate: t.pitchRate,
    loudnessRate: t.loudnessRate,
    referenceImageUrl: t.referenceImageUrl ?? undefined,
    createTime: t.createTime,
  };
}

function pickTaskFromTasksResp(data: unknown): VoicePerformanceTaskItem | null {
  if (!data || typeof data !== "object") return null;
  const payload = data as VoicePerformanceTaskPageResp & VoicePerformanceTaskItem;
  if (Array.isArray(payload.list) && payload.list.length) return payload.list[0];
  if (payload.taskId != null || payload.status) return payload;
  return null;
}

export const VoiceAi: FC = () => {
  // 引导条
  const [showGuide, setShowGuide] = useState(true);

  // 脚本输入（contentEditable DOM 为唯一事实来源）
  const [charCount, setCharCount] = useState(0);

  // 图片上传
  const [sceneImage, setSceneImage] = useState<string | null>(null);
  const [sceneImageFile, setSceneImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { alertConfig, showSuccess, showError, hideAlert } = useAlert();

  // 音色选择（数字 ID）
  const [publicVoices, setPublicVoices] = useState<VoiceOption[]>([]);
  const [mineVoices, setMineVoices] = useState<VoiceOption[]>([]);
  const [allVoices, setAllVoices] = useState<VoiceOption[]>([]);
  const [selectedVoiceIds, setSelectedVoiceIds] = useState<number[]>([]);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [draftVoiceIds, setDraftVoiceIds] = useState<number[]>([]);
  const [voiceSearch, setVoiceSearch] = useState("");
  const [voiceTab, setVoiceTab] = useState<"public" | "mine">("public");

  // @ 提及浮层（@popperjs/core）
  const [showPopup, setShowPopup] = useState(false);
  const [searchKey, setSearchKey] = useState("");
  const popupRef = useRef<HTMLDivElement>(null);
  // 音色试听
  const [previewVoiceId, setPreviewVoiceId] = useState<number | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const popperRef = useRef<PopperInstance | null>(null);
  const popupFrameRef = useRef(0);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastValidHtmlRef = useRef("");

  // Tab
  const [activeTab, setActiveTab] = useState<"templates" | "works">("templates");

  // 作品历史（从 API 获取）
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [worksScriptSearch, setWorksScriptSearch] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const pollTimerRef = useRef<number | null>(null);
  const pollingTaskIdsRef = useRef<Set<number>>(new Set());
  const [isPolishing, setIsPolishing] = useState(false);
  const [polishBackup, setPolishBackup] = useState<string | null>(null);
  const [deletingTaskIds, setDeletingTaskIds] = useState<number[]>([]);

  // 模板
  const [templates, setTemplates] = useState<VoicePerformanceTemplate[]>([]);

  // 播放状态
  const [playingId, setPlayingId] = useState<number | null>(null);
  const [playProgress, setPlayProgress] = useState(0);
  const [templateDurations, setTemplateDurations] = useState<Record<number, number>>({});

  // 作品详情
  const [detailItem, setDetailItem] = useState<HistoryItem | null>(null);
  const [taskDetail, setTaskDetail] = useState<VoicePerformanceTaskItem | null>(null);

  // 语速/音调/音量（与 API 字段对齐）
  const [speechRate, setSpeechRate] = useState(0);     // -50 ~ 100，0=正常
  const [pitchRate, setPitchRate] = useState(0);       // -12 ~ 12，0=正常
  const [loudnessRate, setLoudnessRate] = useState(0); // -50 ~ 100，0=正常

  // ===== 引导条 =====
  const dismissGuide = useCallback(() => {
    setShowGuide(false);
  }, []);

  // ===== 播放控制（单个持久 audio 元素，只换 src） =====
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // 初始化持久 audio
  useEffect(() => {
    const audio = new Audio();
    audio.preload = "auto";
    const onTimeupdate = () => {
      if (audio.duration) setPlayProgress((audio.currentTime / audio.duration) * 100);
    };
    const onLoadedMetadata = () => {
      const rawPlayingId = Number(audio.dataset.playingId);
      if (Number.isFinite(rawPlayingId) && rawPlayingId < 0 && audio.duration) {
        const templateId = Math.abs(rawPlayingId) - TEMPLATE_PLAY_ID_OFFSET;
        setTemplateDurations((prev) => ({ ...prev, [templateId]: audio.duration }));
      }
    };
    const onEnded = () => { setPlayingId(null); setPlayProgress(0); };
    const onError = () => { setPlayingId(null); setPlayProgress(0); };
    audio.addEventListener("timeupdate", onTimeupdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    audioRef.current = audio;
    return () => {
      audio.removeEventListener("timeupdate", onTimeupdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audio.pause();
      audio.src = "";
      audioRef.current = null;
    };
  }, []);

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.currentTime = 0; }
    setPlayingId(null);
    setPlayProgress(0);
  }, []);

  const startPlayback = useCallback(
    (id: number, audioUrl?: string) => {
      if (!audioUrl) return;
      const audio = audioRef.current;
      if (!audio) return;
      // 切歌：直接换 src
      audio.dataset.playingId = String(id);
      audio.src = audioUrl;
      audio.currentTime = 0;
      audio.play().catch(() => { /* 浏览器阻止自动播放时忽略 */ });
      setPlayingId(id);
      setPlayProgress(0);
    },
    []
  );

  const togglePlay = useCallback(
    (id: number, audioUrl?: string) => {
      if (playingId === id && audioRef.current && !audioRef.current.paused) {
        stopPlayback();
      } else {
        startPlayback(id, audioUrl);
      }
    },
    [playingId, stopPlayback, startPlayback]
  );

  const toggleDetailPlay = useCallback(() => {
    if (!detailItem) return;
    togglePlay(detailItem.id, detailItem.audioUrl);
  }, [detailItem, togglePlay]);

  // 离开我的作品页签时停止播放
  useEffect(() => {
    if (activeTab !== "works") stopPlayback();
  }, [activeTab, stopPlayback]);

  // 关闭详情弹窗时停止播放
  useEffect(() => {
    if (!detailItem) stopPlayback();
  }, [detailItem, stopPlayback]);

  // ===== 音色弹窗（VOICES 模式） =====
  const openVoiceModal = useCallback(() => {
    // 打开新增音色前停止当前模板/作品播放，避免与弹窗内试听重叠
    stopPlayback();
    // 仅打开弹窗；图片与音色互斥的清图动作放到 confirmVoiceSelection 里实际确认勾选时再执行
    setDraftVoiceIds([...selectedVoiceIds]);
    setVoiceSearch("");
    setVoiceTab("public");
    setVoiceModalOpen(true);
  }, [selectedVoiceIds, stopPlayback]);

  // 音色试听控制
  const stopVoicePreviews = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewVoiceId(null);
  }, []);

  const closeVoiceModal = useCallback(() => {
    stopVoicePreviews();
    setVoiceModalOpen(false);
  }, [stopVoicePreviews]);

  const toggleDraftVoice = useCallback(
    (id: number) => {
      stopVoicePreviews();
      setDraftVoiceIds((prev) => {
        const idx = prev.indexOf(id);
        if (idx !== -1) {
          return prev.filter((v) => v !== id);
        }
        if (prev.length >= MAX_VOICES) return prev;
        return [...prev, id];
      });
    },
    [stopVoicePreviews]
  );

  // ===== 编辑器内音色 span 的 removed 状态切换 =====
  const setEditorVoicesRemoved = useCallback((ids: number[], removed: boolean) => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.querySelectorAll("span[voice]").forEach((span) => {
      const id = Number(span.getAttribute("voice"));
      if (!ids.includes(id)) return;
      const cls = (span.getAttribute("class") || "").trim().split(/\s+/).filter(Boolean);
      if (removed) {
        if (!cls.includes("removed")) {
          span.setAttribute("class", [...cls, "removed"].join(" ").trim());
        }
      } else {
        const next = cls.filter((c) => c !== "removed");
        span.setAttribute("class", next.length > 0 ? next.join(" ") : "neutral");
      }
    });
    lastValidHtmlRef.current = editor.innerHTML;
    setCharCount(countLiveChars(editor));
  }, []);

  // ===== 图片上传（IMAGE 模式） =====
  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    // 保持原始 File 对象用于 multipart 提交
    setSceneImageFile(file);
    // 生成预览
    const reader = new FileReader();
    reader.onload = (ev) => {
      setSceneImage(ev.target?.result as string);
      // 图片模式与音色互斥：上传图片时清空已选音色，并把编辑器里现有 chip 标灰
      setEditorVoicesRemoved(selectedVoiceIds, true);
      setSelectedVoiceIds([]);
    };
    reader.readAsDataURL(file);
  }, [selectedVoiceIds, setEditorVoicesRemoved]);

  const clearImage = useCallback(() => {
    setSceneImage(null);
    setSceneImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const confirmVoiceSelection = useCallback(() => {
    const nextIds = [...draftVoiceIds];
    const removedIds = selectedVoiceIds.filter((id) => !nextIds.includes(id));
    const restoredIds = nextIds.filter((id) => !selectedVoiceIds.includes(id));

    setEditorVoicesRemoved(removedIds, true);
    setEditorVoicesRemoved(restoredIds, false);

    // 真正确认勾选音色后才清空图片（图片与音色互斥）
    if (sceneImage || sceneImageFile) {
      setSceneImage(null);
      setSceneImageFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }

    setSelectedVoiceIds(nextIds);
    setVoiceModalOpen(false);
  }, [draftVoiceIds, sceneImage, sceneImageFile, selectedVoiceIds, setEditorVoicesRemoved]);

  const removeSelectedVoice = useCallback((id: number) => {
    setEditorVoicesRemoved([id], true);
    setSelectedVoiceIds((prev) => prev.filter((item) => item !== id));
  }, [setEditorVoicesRemoved]);

  const visibleVoices = voiceTab === "public" ? publicVoices : mineVoices;
  const filteredVoices = visibleVoices.filter((v) => {
    if (!voiceSearch.trim()) return true;
    const kw = voiceSearch.toLowerCase();
    return v.name.toLowerCase().includes(kw);
  });

  // ===== 获取公共音色列表 =====
  useEffect(() => {
    const fn = async () => {
      try {
        const res = await voicePerformanceVoicesApi();
        if (res.code === 0 && Array.isArray(res.data)) {
          setPublicVoices(res.data.map(toVoiceOption));
        }
      } catch {
        // ignore
      }
    };
    void fn();
  }, []);

  // ===== 获取我的音色列表（按 keyword 模糊搜索） =====
  const [mineVoicesLoading, setMineVoicesLoading] = useState(false);
  const lastMineKeywordRef = useRef<string | null>(null);

  const reloadMineVoices = useCallback(async (keyword: string) => {
    if (lastMineKeywordRef.current === keyword) return;
    lastMineKeywordRef.current = keyword;
    setMineVoicesLoading(true);
    try {
      const list = await fetchMineVoiceOptionsApi(keyword);
      setMineVoices(list.map(toMineVoiceOption));
    } finally {
      setMineVoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    void reloadMineVoices("");
  }, [reloadMineVoices]);

  useEffect(() => {
    if (voiceTab !== "mine") return;
    const timer = window.setTimeout(() => {
      void reloadMineVoices(voiceSearch.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [voiceSearch, voiceTab, reloadMineVoices]);

  // ===== 合并 allVoices（用于脚本中的 @音色名 引用） =====
  useEffect(() => {
    setAllVoices([...publicVoices, ...mineVoices]);
  }, [publicVoices, mineVoices]);

  // ===== 获取我的作品 =====
  const fetchTasks = useCallback(async (): Promise<HistoryItem[]> => {
    try {
      const keyword = worksScriptSearch.trim();
      const res = await voicePerformanceTasksApi({
        pageNo: 1,
        pageSize: 50,
        ...(keyword ? { keyword } : {}),
      });
      if (res.code === 0 && res.data) {
        const items: HistoryItem[] = (res.data.list || []).map((t: VoicePerformanceTaskItem) =>
          mapTaskToHistoryItem(t),
        );
        setHistoryItems(items);
        return items;
      }
    } catch {
      // ignore
    }
    return [];
  }, [worksScriptSearch]);

  const fetchTasksRef = useRef(fetchTasks);
  fetchTasksRef.current = fetchTasks;

  const stopTaskPolling = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const pollUnfinishedTasks = useCallback(async () => {
    const ids = [...pollingTaskIdsRef.current];
    if (!ids.length) {
      stopTaskPolling();
      return;
    }
    let stillRunning = false;
    let finished = false;
    for (const taskId of ids) {
      try {
        const res = await voicePerformanceTasksApi({ taskId, pageSize: 1 });
        const task = res.code === 0 ? pickTaskFromTasksResp(res.data) : null;
        if (task && isFailedStatus(task.status) && task.errorMessage) {
          showError(task.errorMessage);
        }
        if (task && isTerminalStatus(task.status)) {
          pollingTaskIdsRef.current.delete(taskId);
          finished = true;
        } else {
          stillRunning = true;
          if (task) {
            const mapped = mapTaskToHistoryItem(task);
            setHistoryItems((prev) =>
              prev.map((item) =>
                item.id === taskId || item.id === mapped.id ? { ...item, ...mapped } : item,
              ),
            );
            setDetailItem((prev) =>
              prev && (prev.id === taskId || prev.id === mapped.id) ? { ...prev, ...mapped } : prev,
            );
          }
        }
      } catch {
        stillRunning = true;
      }
    }
    if (finished) {
      await fetchTasksRef.current();
      triggerPointsRefresh();
      triggerMembershipRefresh();
    }
    if (pollingTaskIdsRef.current.size > 0) stillRunning = true;
    if (stillRunning) {
      pollTimerRef.current = window.setTimeout(() => {
        void pollUnfinishedTasks();
      }, 3000);
    } else {
      stopTaskPolling();
    }
  }, [showError, stopTaskPolling]);

  const ensurePolling = useCallback(
    (taskIds: number[]) => {
      let added = false;
      for (const id of taskIds) {
        if (id && !pollingTaskIdsRef.current.has(id)) {
          pollingTaskIdsRef.current.add(id);
          added = true;
        }
      }
      if (!pollingTaskIdsRef.current.size) return;
      if (pollTimerRef.current != null && !added) return;
      stopTaskPolling();
      void pollUnfinishedTasks();
    },
    [pollUnfinishedTasks, stopTaskPolling],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchTasks().then((items) => {
        const unfinishedIds = items
          .filter((item) => isUnfinishedStatus(item.status) && item.id)
          .map((item) => item.id);
        if (unfinishedIds.length) ensurePolling(unfinishedIds);
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [ensurePolling, fetchTasks]);

  useEffect(() => () => stopTaskPolling(), [stopTaskPolling]);

  // ===== 获取模板列表 =====
  useEffect(() => {
    voicePerformanceTemplatesApi().then((res) => {
      if (res.code === 0 && Array.isArray(res.data)) setTemplates(res.data);
    }).catch(() => {});
  }, []);

  // ===== 预加载模板音频时长 =====
  useEffect(() => {
    if (templates.length === 0) return;
    const preloadItems: Array<{ audio: HTMLAudioElement; applyDuration: () => void }> = [];

    templates.forEach((template) => {
      const sampleAudioUrl = resolvePreviewUrl(template.sampleAudioUrl);
      if (!sampleAudioUrl) return;

      const audio = new Audio();
      audio.preload = "metadata";
      const applyDuration = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setTemplateDurations((prev) => prev[template.id] ? prev : { ...prev, [template.id]: audio.duration });
        }
      };
      audio.addEventListener("loadedmetadata", applyDuration);
      audio.addEventListener("durationchange", applyDuration);
      audio.src = sampleAudioUrl;
      audio.load();
      preloadItems.push({ audio, applyDuration });
    });

    return () => {
      preloadItems.forEach(({ audio, applyDuration }) => {
        audio.removeEventListener("loadedmetadata", applyDuration);
        audio.removeEventListener("durationchange", applyDuration);
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      });
    };
  }, [templates]);

  // ===== 预设填充（补全脚本、音色、图片） =====
  const fillPreset = useCallback((tmpl: VoicePerformanceTemplate) => {
    // 切换模板时先清空上一套模板带入的音色、图片和 @ 候选状态
    setSelectedVoiceIds([]);
    setDraftVoiceIds([]);
    setSceneImage(null);
    setSceneImageFile(null);
    setShowPopup(false);
    setSearchKey("");
    if (popperRef.current) {
      popperRef.current.destroy();
      popperRef.current = null;
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    // 应用模板替换整个内容，清除润色撤销备份
    setPolishBackup(null);

    // 模板不携带语速/音调/音量，找不到就默认 0（正常）
    setSpeechRate(0);
    setPitchRate(0);
    setLoudnessRate(0);

    const editor = editorRef.current;
    if (editor) {
      const html = sanitizeScriptHtml(tmpl.script, allVoices);
      editor.innerHTML = html;
      lastValidHtmlRef.current = editor.innerHTML;
      setCharCount(countLiveChars(editor));
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
      editor.scrollIntoView({ behavior: "smooth", block: "center" });
      editor.focus();
    }

    const templateVoiceIds = normalizeVoiceIds(tmpl.voiceIds);
    if (tmpl.coverImageUrl) {
      setSceneImage(tmpl.coverImageUrl);
    } else if (templateVoiceIds.length > 0) {
      setSelectedVoiceIds(templateVoiceIds);
      setDraftVoiceIds(templateVoiceIds);
    }
  }, [allVoices]);
  // ===== @ 提及处理（@popperjs/core） =====
  const destroyPopper = useCallback(() => {
    if (popperRef.current) { popperRef.current.destroy(); popperRef.current = null; }
  }, []);

  const updatePopper = useCallback(() => {
    const popup = popupRef.current;
    const editor = editorRef.current;
    if (!popup || !editor) return destroyPopper();

    // 锚定光标本身：getBoundingClientRect 每次重新读当前 caret rect，popper.update() 即可跟随
    const caretReference: VirtualElement = {
      contextElement: editor,
      getBoundingClientRect: () => {
        const caret = getCaretRect();
        return caret || editor.getBoundingClientRect();
      },
    };

    if (popperRef.current) {
      void popperRef.current.update();
      return;
    }

    popperRef.current = createPopper(caretReference, popup, {
      placement: "bottom-start",
      strategy: "fixed",
      modifiers: [
        { name: "flip", enabled: true },
        { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
        { name: "offset", options: { offset: [0, 4] } },
      ],
    });
  }, [destroyPopper]);

  // ===== 编辑器输入：字数统计 + 超长回滚 + @ 提及检测 =====
  const handleEditorInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const live = countLiveChars(editor);

    if (live > MAX_CHARS) {
      editor.innerHTML = lastValidHtmlRef.current;
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(range);
      }
      setCharCount(countLiveChars(editor));
      return;
    }

    lastValidHtmlRef.current = editor.innerHTML;
    setCharCount(live);
    // 用户手动编辑后清除润色撤销备份，避免覆盖用户修改
    setPolishBackup(null);

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      setShowPopup(false);
      return;
    }
    const range = sel.getRangeAt(0);
    const textBefore = getTextBeforeCaret(range);
    const lastAt = textBefore.lastIndexOf("@");
    if (lastAt === -1 || selectedVoiceIds.length === 0) {
      setShowPopup(false);
      return;
    }
    const keyword = textBefore.slice(lastAt + 1).replace(/\s/g, "");
    setSearchKey(keyword);
    setShowPopup(true);
    requestAnimationFrame(() => updatePopper());
  }, [selectedVoiceIds, updatePopper]);

  const closeMention = useCallback(() => {
    setShowPopup(false);
    destroyPopper();
  }, [destroyPopper]);

  const insertMention = useCallback((voice: VoiceOption) => {
    const editor = editorRef.current;
    if (!editor) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    // 防御：光标处应为折叠选区（@ 提及态）；若为展开选区则放弃本次插入
    if (!range.collapsed) return;

    // 边界检查放在 @ 删除之前：按「替换后的最终长度」判断，失败时保留用户输入的 @
    const mentionPos = findMentionAtPosition(range);
    let oldMentionLength = 0;
    if (mentionPos) {
      const textBefore = getTextBeforeCaret(range);
      const at = textBefore.lastIndexOf("@");
      if (at !== -1) oldMentionLength = normalizePlain(textBefore.slice(at)).length;
    }
    const finalLength = countLiveChars(editor) - oldMentionLength + getVoiceChipTextLength(voice);
    if (finalLength > MAX_CHARS) {
      closeMention();
      showError("超出输入框限制最大字数，音色插入失败");
      return;
    }

    // 删除光标前的 @关键字（若存在）：支持 @ 位于非 TEXT_NODE 容器 / 跨文本节点 / 嵌套内联元素
    if (mentionPos) {
      const delRange = document.createRange();
      delRange.setStart(mentionPos.node, mentionPos.offset);
      delRange.setEnd(range.startContainer, range.startOffset);
      delRange.deleteContents();
      range.setStart(mentionPos.node, mentionPos.offset);
      range.collapse(true);
    }

    const span = document.createElement("span");
    span.setAttribute("contenteditable", "false");
    span.setAttribute("voice", String(voice.id));
    span.setAttribute("class", genderClass(voice));
    span.textContent = `@${voice.name}`;

    range.deleteContents();
    range.insertNode(span);

    const caretRange = document.createRange();
    caretRange.setStartAfter(span);
    caretRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(caretRange);

    lastValidHtmlRef.current = editor.innerHTML;
    setCharCount(countLiveChars(editor));
    // 插入音色属于手动编辑，清除润色撤销备份
    setPolishBackup(null);
    setShowPopup(false);
    destroyPopper();
    editor.focus();
  }, [closeMention, destroyPopper, showError]);

  // 退格：光标紧贴「span 后紧随的空格」时，连同空格整块删除音色
  const removePrecedingChipWithSpace = useCallback((): boolean => {
    const editor = editorRef.current;
    if (!editor) return false;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return false;
    const range = sel.getRangeAt(0);
    const container = range.startContainer;
    if (container.nodeType !== Node.TEXT_NODE) return false;
    const textNode = container as Text;
    const offset = range.startOffset;
    const before = textNode.data.slice(0, offset);
    if (before.length === 0 || before.trim() !== "") return false;
    const prev = textNode.previousSibling;
    if (!prev || !(prev instanceof HTMLElement) || !prev.hasAttribute("voice")) return false;

    const chipStart = prev.previousSibling;
    textNode.splitText(offset);
    prev.remove();
    textNode.remove();
    const newRange = document.createRange();
    if (chipStart) {
      newRange.setStartAfter(chipStart);
    } else {
      newRange.setStart(editor, 0);
    }
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    lastValidHtmlRef.current = editor.innerHTML;
    setCharCount(countLiveChars(editor));
    // Backspace 删除音色属于手动编辑，清除润色撤销备份
    setPolishBackup(null);
    return true;
  }, []);

  const handleEditorKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && showPopup) {
      closeMention();
      e.preventDefault();
      return;
    }
    if (e.key === "Enter" && showPopup && !e.nativeEvent.isComposing) {
      e.preventDefault();
      const selected = allVoices.filter((v) => selectedVoiceIds.includes(v.id));
      const filtered = selected.filter((v) => v.name.includes(searchKey));
      if (filtered.length > 0) insertMention(filtered[0]);
      return;
    }
    if ((e.key === "Backspace" || e.key === "Delete") && e.nativeEvent.isComposing) {
      return;
    }
    if (e.key === "Backspace" && removePrecedingChipWithSpace()) {
      e.preventDefault();
    }
  }, [showPopup, searchKey, allVoices, selectedVoiceIds, insertMention, closeMention, removePrecedingChipWithSpace]);

  // 弹层打开时持续跟随光标
  useEffect(() => {
    if (!showPopup) return;
    const frame = requestAnimationFrame(() => updatePopper());
    return () => cancelAnimationFrame(frame);
  }, [showPopup, searchKey, updatePopper]);

  // 弹层打开时：滚动/缩放/选区变化 → 重新定位；光标已不在 @ 提及流内则关闭
  useEffect(() => {
    if (!showPopup) return;
    const editor = editorRef.current;
    const onResize = () => updatePopper();
    const onScroll = () => updatePopper();
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) {
        closeMention();
        return;
      }
      const textBefore = getTextBeforeCaret(sel.getRangeAt(0));
      if (textBefore.lastIndexOf("@") === -1) {
        closeMention();
        return;
      }
      // selectionchange 极频繁，用 rAF 节流重定位
      cancelAnimationFrame(popupFrameRef.current);
      popupFrameRef.current = requestAnimationFrame(() => updatePopper());
    };
    window.addEventListener("resize", onResize);
    editor?.addEventListener("scroll", onScroll);
    document.addEventListener("selectionchange", onSelectionChange);
    return () => {
      window.removeEventListener("resize", onResize);
      editor?.removeEventListener("scroll", onScroll);
      document.removeEventListener("selectionchange", onSelectionChange);
      cancelAnimationFrame(popupFrameRef.current);
      destroyPopper();
    };
  }, [showPopup, closeMention, updatePopper, destroyPopper]);

  // 弹窗打开时锁定输入框内部滚动，关闭时恢复。
  // 保持 overflow-y:auto 让滚动条常驻不消失、不跳版，用「拦截滚轮 + 冻结 scrollTop」禁止滚动
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !showPopup) return;
    const frozenScrollTop = editor.scrollTop;
    const preventWheel = (e: WheelEvent) => {
      e.preventDefault();
    };
    const freezeScroll = () => {
      if (editor.scrollTop !== frozenScrollTop) editor.scrollTop = frozenScrollTop;
    };
    editor.addEventListener("wheel", preventWheel, { passive: false });
    editor.addEventListener("scroll", freezeScroll);
    return () => {
      editor.removeEventListener("wheel", preventWheel);
      editor.removeEventListener("scroll", freezeScroll);
    };
  }, [showPopup]);


  // ===== 检测当前模式 =====
  const currentReferenceType = sceneImageFile ? "IMAGE" : selectedVoiceIds.length > 0 ? "VOICES" : "TEXT";

  // ===== AI 润色脚本 =====
  const handlePolish = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return;
    if (isPolishing || isGenerating) return;

    const requestScript = serializeEditorScript(editor);
    if (!requestScript.trim()) {
      showError("请先输入脚本内容");
      return;
    }

    // 备份原文（调用前，避免异步竞态），供用户撤销润色
    setPolishBackup(editor.innerHTML);

    setIsPolishing(true);
    try {
      const image =
        currentReferenceType === "IMAGE" ? sceneImageFile ?? undefined : undefined;
      const res = await voicePerformancePolishApi(
        { referenceType: currentReferenceType, script: requestScript },
        image,
      );
      if (res.code === 0 && res.data?.optimizedPrompt) {
        let optimized = res.data.optimizedPrompt;
        // 安全网：响应超 1500 字符时截断
        if (optimized.length > MAX_CHARS) optimized = optimized.slice(0, MAX_CHARS);
        const html = sanitizeScriptHtml(optimized, allVoices);
        editor.innerHTML = html;
        lastValidHtmlRef.current = editor.innerHTML;
        setCharCount(countLiveChars(editor));
        // 重置光标到末尾并聚焦（参照 fillPreset）
        const range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        editor.scrollIntoView({ behavior: "smooth", block: "center" });
        editor.focus();
        showSuccess("润色完成");
      } else {
        setPolishBackup(null);
        showError(res.msg || "润色失败，请稍后重试");
      }
    } catch (error) {
      setPolishBackup(null);
      console.error("润色失败:", error);
      showError("润色失败，请稍后重试");
    } finally {
      setIsPolishing(false);
    }
  }, [isPolishing, isGenerating, currentReferenceType, sceneImageFile, allVoices, showError, showSuccess]);

  // ===== 撤销润色 =====
  const handleRevertPolish = useCallback(() => {
    if (polishBackup === null) return;
    const editor = editorRef.current;
    if (!editor) return;

    editor.innerHTML = polishBackup;
    lastValidHtmlRef.current = editor.innerHTML;
    setCharCount(countLiveChars(editor));
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    const sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
    editor.focus();

    setPolishBackup(null);
    showSuccess("已恢复原文");
  }, [polishBackup, showSuccess]);

  const beginGeneratedTask = useCallback(
    (taskId: number, item: HistoryItem) => {
      const pending: HistoryItem = {
        ...item,
        id: taskId,
        status: "processing",
        audioUrl: undefined,
        duration: 0,
      };
      setHistoryItems((prev) => [pending, ...prev.filter((x) => x.id !== taskId)]);
      setActiveTab("works");
      triggerPointsRefresh();
      ensurePolling([taskId]);
    },
    [ensurePolling],
  );

  // ===== 生成配音（支持三种模式） =====
  const handleGenerate = useCallback(async () => {
    const editor = editorRef.current;
    if (!editor) return;
    const requestScript = serializeEditorScript(editor);

    if (!requestScript.trim()) return;
    if (isGenerating) return;
    setIsGenerating(true);

    try {
      const audioConfig = {
        format: "MP3" as const,
        sampleRate: 24000,
        speechRate,
        pitchRate,
        loudnessRate,
        enableSubtitle: true,
      };
      const referenceType = currentReferenceType;

      if (referenceType === "IMAGE") {
        const reqJson = JSON.stringify({
          referenceType: "IMAGE",
          script: requestScript,
          audioConfig,
        });
        const res = await voicePerformanceGenerateWithImageApi(reqJson, sceneImageFile!);
        if (res.code === 0 && res.data?.taskId) {
          beginGeneratedTask(res.data.taskId, {
            id: res.data.taskId,
            text: requestScript,
            voices: "图片参考模式",
            duration: res.data.duration || 0,
            status: res.data.status || "processing",
            audioUrl: resolvePreviewUrl(res.data.outputAudioUrl),
            speechRate,
            pitchRate,
            loudnessRate,
            referenceImageUrl: res.data.referenceImageUrl,
            createTime: new Date().toISOString(),
          });
        }
      } else {
        const body: Parameters<typeof voicePerformanceGenerateApi>[0] = {
          referenceType,
          script: requestScript,
          audioConfig,
        };
        if (referenceType === "VOICES") {
          body.voices = selectedVoiceIds
            .map((id) => allVoices.find((v) => v.id === id))
            .filter((v): v is VoiceOption => Boolean(v))
            .map((v) => ({ voiceId: v.id, voiceSourceType: voiceSourceTypeOf(v) }));
        }
        const res = await voicePerformanceGenerateApi(body);
        if (res.code === 0 && res.data?.taskId) {
          const voiceNames =
            selectedVoiceIds
              .map((id) => allVoices.find((v) => v.id === id)?.name)
              .filter(Boolean)
              .join("、") || "无音色";
          beginGeneratedTask(res.data.taskId, {
            id: res.data.taskId,
            text: requestScript,
            voices: voiceNames,
            duration: res.data.duration || 0,
            status: res.data.status || "processing",
            audioUrl: resolvePreviewUrl(res.data.outputAudioUrl),
            speechRate,
            pitchRate,
            loudnessRate,
            createTime: new Date().toISOString(),
          });
        }
      }
    } catch {
      // silent
    } finally {
      setIsGenerating(false);
    }
  }, [
    allVoices,
    beginGeneratedTask,
    currentReferenceType,
    isGenerating,
    loudnessRate,
    pitchRate,
    sceneImageFile,
    selectedVoiceIds,
    speechRate,
  ]);

  // ===== 作品详情（打开时从 API 补全信息） =====
  const openDetail = useCallback((item: HistoryItem) => {
    setDetailItem(item);
    setTaskDetail(null);
    // 获取单条任务详情
    voicePerformanceTasksApi({ taskId: item.id, pageSize: 1 }).then((res) => {
      if (res.code === 0 && res.data) {
        const detail = Array.isArray(res.data.list) ? res.data.list[0] : res.data;
        if (detail) {
          const taskDetailItem = detail as VoicePerformanceTaskItem;
          if (isFailedStatus(taskDetailItem.status) && taskDetailItem.errorMessage) {
            showError(taskDetailItem.errorMessage);
          }
          setTaskDetail(taskDetailItem);
        }
      }
    }).catch(() => {});
  }, [showError]);

  const closeDetail = useCallback(() => {
    setDetailItem(null);
    setTaskDetail(null);
  }, []);

  const handleDeleteTask = useCallback(async (item: HistoryItem) => {
    if (!item.id || deletingTaskIds.includes(item.id)) return;
    setDeletingTaskIds((prev) => [...prev, item.id]);
    try {
      const res = await voicePerformanceTaskDeleteApi({ taskIds: [item.id] });
      if (res.code === 0) {
        const deletedCount = res.data?.deletedCount ?? 0;
        const skippedIds = res.data?.skippedIds ?? [];
        if (deletedCount > 0 && !skippedIds.includes(item.id)) {
          pollingTaskIdsRef.current.delete(item.id);
          setHistoryItems((prev) => prev.filter((historyItem) => historyItem.id !== item.id));
          if (playingId === item.id) stopPlayback();
          if (detailItem?.id === item.id) closeDetail();
        } else {
          void fetchTasks();
        }
      } else {
        window.alert(res.msg || "删除失败，请稍后重试");
      }
    } catch {
      window.alert("删除失败，请稍后重试");
    } finally {
      setDeletingTaskIds((prev) => prev.filter((taskId) => taskId !== item.id));
    }
  }, [closeDetail, deletingTaskIds, detailItem?.id, fetchTasks, playingId, stopPlayback]);

  const downloadHistoryAudio = useCallback(async (item: HistoryItem) => {
    if (!item.audioUrl) return;

    const fileName = `voice_${item.id}.mp3`;
    const triggerDirectDownload = (url: string) => {
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    try {
      const resp = await fetch(item.audioUrl);
      if (!resp.ok) throw new Error("audio download failed");
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      triggerDirectDownload(item.audioUrl);
    }
  }, []);
  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m < 10 ? "0" : ""}${m}:${s < 10 ? "0" : ""}${s}`;
  };

  // ===== Lucide 图标 SVG =====
  const IconX = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  const IconPlus = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );

  const IconImagePlus = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
      <line x1="23" y1="3" x2="17" y2="9" />
      <line x1="17" y1="3" x2="23" y2="9" />
    </svg>
  );

  const IconArrowRight = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );

  const IconSparkles = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
      <path d="M5 3v4" />
      <path d="M3 5h4" />
      <path d="M19 17v4" />
      <path d="M17 19h4" />
    </svg>
  );

  const IconUndo = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7v6h6" />
      <path d="M21 17a9 9 0 0 0-15-6.7L3 13" />
    </svg>
  );

  const IconSearch = () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );

  const IconPlay = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );

  const IconPause = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="6" y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  );

  const IconDownload = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );

  const IconTrash = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );

  const IconCheck = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const IconAudioLines = () => (
    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );

  return (
    <div className="voice-ai-page">
      <AlertMessage config={alertConfig} onClose={hideAlert} />
      <div className="voice-ai-shell">
        {/* ===== 顶部标题 ===== */}
        <section className="voice-ai-hero">
          <div>
            <div className="voice-ai-kicker">Audio Scene Studio</div>
            <h1>AI 音频</h1>
            <p>
              把对白、情绪、环境音和节奏写进同一段脚本，系统会按你指定的音色完成整段音频合成。
            </p>
          </div>
        </section>

        {/* ===== 新手引导条 ===== */}
        {showGuide && (
          <div className="voice-ai-guide">
            <span className="voice-ai-guide-text">
              <strong>小提示：</strong>写清场景与角色，可用 <strong>@</strong>{" "}
              引用音色为每个角色标定指定音色，最后点击生成音频。
            </span>
            <button className="voice-ai-guide-close" onClick={dismissGuide} title="不再显示">
              <IconX />
            </button>
          </div>
        )}

        {/* ===== 核心编辑区 ===== */}
        <section>
          <div className={`voice-ai-editor${isPolishing || isGenerating ? " voice-ai-editor--busy" : ""}`}>
            <div className="voice-ai-editor-head">
              <div>
                <h3>脚本编排</h3>
                <p>支持旁白、多人对白、口播、广播剧等内容。写得越明确，生成结果越稳定。</p>
              </div>
              <div className="voice-ai-ref-area">
                <span className="voice-ai-ref-label">已选音色</span>
                <span className="voice-ai-ref-count">{selectedVoiceIds.length} / {MAX_VOICES}</span>
                <button className="voice-ai-add-voice-btn" onClick={openVoiceModal}>
                  <IconPlus />
                  新增音色
                </button>
                {selectedVoiceIds.length > 0 && (
                  <div className="voice-ai-selected-chips">
                    {selectedVoiceIds.map((id) => {
                      const v = allVoices.find((x) => x.id === id);
                      return v ? (
                        <span key={id} className="voice-ai-chip">
                          @{v.name}
                          <span
                            className="voice-ai-chip-x"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSelectedVoice(id);
                            }}
                          >
                            ×
                          </span>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* 脚本输入区 */}
            <div className="voice-ai-script-area">
              <label
                htmlFor="voice-ai-file-input"
                className={`voice-ai-image-upload${sceneImage ? " has-image" : ""}`}
                title="上传场景参考图"
              >
                {sceneImage && (
                  <img className="voice-ai-image-preview" src={sceneImage} alt="场景参考图" />
                )}
                <span className="voice-ai-upload-empty">
                  <IconImagePlus />
                </span>
                <span className="voice-ai-upload-empty voice-ai-image-upload-text">上传图片</span>
                {sceneImage && (
                  <button
                    type="button"
                    className="voice-ai-image-clear"
                    onClick={(e) => {
                      e.preventDefault();
                      clearImage();
                    }}
                  >
                    <IconX />
                  </button>
                )}
              </label>
              <input
                ref={fileInputRef}
                id="voice-ai-file-input"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleImageUpload}
              />
              <div
                className="voice-ai-textarea-wrap"
                style={{ position: "relative" }}
                aria-busy={isPolishing}
              >
                {charCount === 0 && !(editorRef.current && editorRef.current.querySelector("span[voice]")) && (
                  <div className="voice-ai-script-placeholder" aria-hidden="true">
                    输入你想生成的音频脚本。示例：@温和旁白 以安静、克制的语气介绍一座清晨的城市；@青年男声 接一句自然的回应，整体约 90 秒。
                  </div>
                )}
                <div
                  ref={editorRef}
                  className="voice-ai-script-input"
                  contentEditable={!isPolishing}
                  suppressContentEditableWarning
                  spellCheck={false}
                  autoCorrect="off"
                  autoCapitalize="off"
                  onInput={handleEditorInput}
                  onKeyDown={handleEditorKeyDown}
                  onPaste={(e) => {
                    e.preventDefault();
                    const editor = editorRef.current;
                    if (!editor) return;
                    const sel = window.getSelection();
                    if (!sel || sel.rangeCount === 0) return;
                    const range = sel.getRangeAt(0);
                    let text = e.clipboardData.getData("text/plain").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
                    const currentLen = countLiveChars(editor);
                    const selectionLen = range.toString().length;
                    const remaining = MAX_CHARS - (currentLen - selectionLen);
                    if (remaining <= 0) return;
                    if (text.length > remaining) text = text.slice(0, remaining);
                    insertPlainTextAtRange(range, text);
                    sel.removeAllRanges();
                    sel.addRange(range);
                    lastValidHtmlRef.current = editor.innerHTML;
                    setCharCount(countLiveChars(editor));
                    // 粘贴属于手动编辑，清除润色撤销备份
                    setPolishBackup(null);
                  }}
                  onBlur={() => setTimeout(closeMention, 200)}
                />
                {/* @ 提及浮层（createPortal 挂载到 body，@popperjs/core 定位） */}
                {showPopup && createPortal(
                  (() => {
                    const selected = allVoices.filter((v) => selectedVoiceIds.includes(v.id));
                    const filtered = selected.filter((v) => v.name.includes(searchKey));
                    if (filtered.length === 0) return null;
                    return (
                      <div ref={popupRef} className="voice-ai-mention-popover">
                        {filtered.map((v) => {
                          const dotClass =
                            v.genderType === "male"
                              ? "voice-ai-mention-dot blue"
                              : v.genderType === "female"
                              ? "voice-ai-mention-dot pink"
                              : "voice-ai-mention-dot neutral";
                          const shortGender = v.genderType === "female" ? "女" : v.genderType === "male" ? "男" : "";
                          return (
                            <div
                              key={v.id}
                              className="voice-ai-mention-item"
                              onMouseDown={(e) => { e.preventDefault(); insertMention(v); }}
                            >
                              <span
                                className={dotClass}
                              />
                              <span>{v.name}</span>
                              {shortGender ? (
                                <span className="voice-ai-mention-gender">{shortGender}</span>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })(),
                  document.body
                )}
                <span className="voice-ai-char-count">
                  {charCount} / {MAX_CHARS}
                </span>
              </div>
              {isPolishing && (
                <div className="voice-ai-polish-overlay" role="status" aria-live="polite">
                  <PolishLoadingSparkles text="AI 润色中…" />
                </div>
              )}
            </div>

            {/* 底部控制栏 */}
            <div className="voice-ai-editor-bar">
              <div className="voice-ai-params">
                <AudioSlider
                  label="语速"
                  min={-50} max={100}
                  value={speechRate}
                  onChange={setSpeechRate}
                  formatValue={(v) => v === 0 ? "正常" : v > 0 ? `快 +${v}` : `慢 ${v}`}
                />
                <AudioSlider
                  label="音调"
                  min={-12} max={12}
                  value={pitchRate}
                  onChange={setPitchRate}
                  formatValue={(v) => v === 0 ? "正常" : v > 0 ? `偏高 +${v}` : `偏低 ${v}`}
                />
                <AudioSlider
                  label="音量"
                  min={-50} max={100}
                  value={loudnessRate}
                  onChange={setLoudnessRate}
                  formatValue={(v) => v === 0 ? "正常" : v > 0 ? `大 +${v}` : `小 ${v}`}
                />
              </div>
              <div className="voice-ai-actions">
                {polishBackup === null ? (
                  <button
                    className="voice-ai-polish-btn"
                    type="button"
                    onClick={handlePolish}
                    title="AI 润色脚本"
                    disabled={isPolishing || isGenerating || charCount === 0}
                  >
                    <IconSparkles />
                    <span>{isPolishing ? "润色中…" : "润色"}</span>
                  </button>
                ) : (
                  <button
                    className="voice-ai-polish-btn voice-ai-revert-btn"
                    type="button"
                    onClick={handleRevertPolish}
                    title="恢复润色前原文"
                    disabled={isPolishing || isGenerating}
                  >
                    <IconUndo />
                    <span>撤销</span>
                  </button>
                )}
                <button className="voice-ai-gen-btn" onClick={handleGenerate} title="生成音频" disabled={isGenerating || isPolishing}>
                  <span>{isGenerating ? "生成中…" : "生成音频"}</span>
                  <IconArrowRight />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ===== 作品区域（模板 + 我的作品） ===== */}
        <section className="voice-ai-work">
          <div className="voice-ai-tabs">
            <button
              className={`voice-ai-tab${activeTab === "templates" ? " active" : ""}`}
              onClick={() => {
                if (activeTab !== "templates") stopPlayback();
                setActiveTab("templates");
              }}
            >
              模板
            </button>
            <button
              className={`voice-ai-tab${activeTab === "works" ? " active" : ""}`}
              onClick={() => {
                if (activeTab !== "works") stopPlayback();
                setActiveTab("works");
              }}
            >
              我的作品
            </button>
          </div>

          {/* 模板面板 */}
          <div className={`voice-ai-panel${activeTab !== "templates" ? " voice-ai-panel--hidden" : ""}`}>
            <div className="voice-ai-preset-grid">
              {templates.length === 0 ? (
                <div className="voice-ai-empty" style={{ gridColumn: "1 / -1" }}>
                  <IconAudioLines />
                  <span>暂无模板</span>
                </div>
              ) : (
                templates.map((t) => {
                  const templatePlayId = getTemplatePlaybackId(t.id);
                  const sampleAudioUrl = resolvePreviewUrl(t.sampleAudioUrl);
                  const isTemplatePlaying = playingId === templatePlayId;
                  const templateDuration = templateDurations[t.id] ?? 0;
                  return (
                    <div
                      key={t.id}
                      className="voice-ai-preset-card"
                      onClick={() => fillPreset(t)}
                      style={t.coverImageUrl ? {
                        backgroundImage: `url(${t.coverImageUrl})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        color: "#fff",
                      } : undefined}
                    >
                      <div
                        className="voice-ai-preset-content"
                        style={t.coverImageUrl ? {
                          background: "rgba(0,0,0,0.45)",
                          borderRadius: 12,
                          padding: 12,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-end",
                        } : undefined}
                      >
                        {t.coverImageUrl ? (
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.title}</div>
                        ) : (
                          <h4>{t.title}</h4>
                        )}
                        <p>{plainScriptText(t.description || t.script.slice(0, 60), allVoices)}...</p>
                        <div className="voice-ai-preset-audio" onClick={(e) => e.stopPropagation()}>
                          <span className="voice-ai-preset-duration">
                            {templateDuration ? formatTime(templateDuration) : "--:--"}
                          </span>
                          <button
                            className="voice-ai-card-play"
                            data-state={isTemplatePlaying ? "pause" : "play"}
                            title={isTemplatePlaying ? "暂停" : "播放"}
                            disabled={!sampleAudioUrl}
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePlay(templatePlayId, sampleAudioUrl);
                            }}
                          >
                            {isTemplatePlaying ? <IconPause /> : <IconPlay />}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })              )}
            </div>
          </div>

          {/* 我的作品面板 */}
          <div className={`voice-ai-panel${activeTab !== "works" ? " voice-ai-panel--hidden" : ""}`}>
            <div className="voice-ai-works-toolbar">
              <div className="voice-ai-works-search">
                <IconSearch />
                <input
                  type="text"
                  placeholder={"\u6309\u811a\u672c\u5185\u5bb9\u641c\u7d22"}
                  value={worksScriptSearch}
                  onChange={(e) => setWorksScriptSearch(e.target.value)}
                />
              </div>
              <span className="voice-ai-history-count" style={{ fontSize: 12, color: "#94a3b8" }}>
                {historyItems.length} 条
              </span>
            </div>
            <div className="voice-ai-history-grid">
              {historyItems.length === 0 ? (
                <div className="voice-ai-empty" style={{ gridColumn: "1 / -1" }}>
                  <IconAudioLines />
                  <span>暂无生成记录</span>
                </div>
              ) : (
                historyItems.map((item) => {
                  const isPlaying = playingId === item.id;
                  const isDeleting = deletingTaskIds.includes(item.id);
                  const isPending = isUnfinishedStatus(item.status);
                  const isFailed = isFailedStatus(item.status);
                  const canDownload = !isPending && !isFailed && !!item.audioUrl;
                  return (
                    <div
                      key={item.id}
                      className={`voice-ai-history-card${isPlaying ? " playing" : ""}${isPending ? " is-generating" : ""}`}
                      onClick={() => openDetail(item)}
                    >
                      {isPending ? (
                        <span className="voice-ai-history-generating" role="status" aria-live="polite">
                          <span className="voice-ai-history-spinner" aria-hidden />
                          正在生成中
                        </span>
                      ) : (
                        <IconAudioLines />
                      )}
                      <div className="voice-ai-history-title">
                        {formatHistoryTitle(item.text, allVoices)}
                      </div>
                      <div className="voice-ai-history-meta">
                        <div className="voice-ai-history-meta-left">
                          {!isPending && item.voices ? (
                            <span className="voice-ai-history-voice-tag">{item.voices}</span>
                          ) : null}
                          <span className="voice-ai-history-duration">
                            {isPending ? "生成中…" : formatTime(item.duration)}
                          </span>
                          {item.createTime ? (
                            <span className="voice-ai-history-time">{formatHistoryTime(item.createTime)}</span>
                          ) : null}
                        </div>
                        <div className="voice-ai-history-actions">
                          {isFailed && <span className="voice-ai-history-failed">失败</span>}
                          <button
                            className="voice-ai-card-download"
                            title="下载"
                            disabled={!canDownload}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canDownload) return;
                              void downloadHistoryAudio(item);
                            }}
                          >
                            <IconDownload />
                          </button>
                          <Popconfirm
                            title="确定要删除这个作品吗？"
                            okText="确定"
                            cancelText="取消"
                            onPopupClick={(e) => {
                              e.stopPropagation();
                            }}
                            onConfirm={() => {
                              void handleDeleteTask(item);
                            }}
                          >
                            <button
                              type="button"
                              className="voice-ai-card-delete"
                              title={isDeleting ? "删除中" : "删除"}
                              disabled={isDeleting}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <IconTrash />
                            </button>
                          </Popconfirm>
                          <button
                            className="voice-ai-card-play"
                            data-state={isPlaying ? "pause" : "play"}
                            title={isPlaying ? "暂停" : "播放"}
                            disabled={!canDownload}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canDownload) return;
                              togglePlay(item.id, item.audioUrl);
                            }}
                          >
                            {isPlaying ? <IconPause /> : <IconPlay />}
                          </button>
                        </div>
                      </div>
                      {/* 作品卡不显示进度条和时间 — 模板卡保留 */}
                      {false && (
                        <div className="voice-ai-card-player">
                          <div className="voice-ai-card-progress">
                            <div
                              className="voice-ai-card-progress-fill"
                              style={{
                                width: isPlaying ? `${playProgress}%` : "0%",
                              }}
                            />
                          </div>
                          <span className="voice-ai-card-time">
                            {isPlaying ? formatTime((playProgress / 100) * item.duration) : formatTime(item.duration)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </div>

      {/* ===== 作品详情弹窗（居中弹窗，与公共音色选择弹窗一致） ===== */}
      <div
        className={`voice-ai-modal-overlay${detailItem ? " show" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeDetail();
        }}
      >
        <div className="voice-ai-modal-content voice-ai-detail-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
          <div className="voice-ai-modal-header">
            <div>
              <h3>作品详情</h3>
            </div>
            <div className="voice-ai-modal-header-actions">
              {detailItem && (
                <Popconfirm
                  title="确定要删除这个作品吗？"
                  okText="确定"
                  cancelText="取消"
                  onPopupClick={(e) => {
                    e.stopPropagation();
                  }}
                  onConfirm={() => {
                    void handleDeleteTask(detailItem);
                  }}
                >
                  <button
                    type="button"
                    className="voice-ai-modal-delete"
                    title={deletingTaskIds.includes(detailItem.id) ? "删除中" : "删除"}
                    disabled={deletingTaskIds.includes(detailItem.id)}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    <IconTrash />
                  </button>
                </Popconfirm>
              )}
              <button className="voice-ai-modal-close" onClick={closeDetail}>
                <IconX />
              </button>
            </div>
          </div>
          <div className="voice-ai-modal-body">
            {detailItem && (
              <>
                <div className="voice-ai-detail-section">
                  <div className="voice-ai-detail-label">音频播放</div>
                  <div className="voice-ai-detail-player">
                    <button
                      className="voice-ai-detail-play-btn"
                      onClick={toggleDetailPlay}
                      disabled={!detailItem.audioUrl}
                    >
                      {playingId === detailItem.id ? <IconPause /> : <IconPlay />}
                    </button>
                    <div className="voice-ai-detail-progress">
                      <div
                        className="voice-ai-detail-progress-fill"
                        style={{
                          width: playingId === detailItem.id ? `${playProgress}%` : "0%",
                        }}
                      />
                    </div>
                    <span className="voice-ai-detail-time">
                      {playingId === detailItem.id
                        ? formatTime((playProgress / 100) * detailItem.duration)
                        : formatTime(detailItem.duration)}
                    </span>
                  </div>
                </div>
                <div className="voice-ai-detail-section">
                  <div className="voice-ai-detail-label">脚本内容</div>
                  <div className="voice-ai-detail-script">{renderScriptHtml(detailItem.text, allVoices)}</div>
                </div>
                <div className="voice-ai-detail-section">
                  <div className="voice-ai-detail-label">引用音色</div>
                  <div className="voice-ai-detail-voices">
                    {(() => {
                      const script = taskDetail?.script || detailItem.text;
                      const tags = extractVoiceTagsFromHtml(script, allVoices);
                      return tags.length > 0 ? tags.map((name) => (
                        <span key={name} className="voice-ai-chip">
                          @{name}
                        </span>
                      )) : <span style={{ fontSize: 13, color: "#94a3b8" }}>无引用音色</span>;
                    })()}
                  </div>
                </div>
                {(() => {
                  const refImageUrl = taskDetail?.referenceImageUrl || detailItem.referenceImageUrl;
                  if (!refImageUrl) return null;
                  return (
                    <div className="voice-ai-detail-section">
                      <div className="voice-ai-detail-label">引用图片</div>
                      <div className="voice-ai-detail-reference-image">
                        <img src={refImageUrl} alt="引用图片" />
                      </div>
                    </div>
                  );
                })()}
                <div className="voice-ai-detail-section">
                  <div className="voice-ai-detail-label">任务状态</div>
                  <div style={{ fontSize: 13, color: "#64748b" }}>
                    {detailItem.status === "success" ? "已完成" :
                     detailItem.status === "failed" ? `失败${detailItem.errorMessage || (taskDetail?.errorMessage ? `：${taskDetail.errorMessage}` : "")}` :
                     isUnfinishedStatus(detailItem.status) ? "正在生成中" :
                     detailItem.status}
                  </div>
                </div>
                <div className="voice-ai-detail-section">
                  <div className="voice-ai-detail-label">参数设置</div>
                  <div className="voice-ai-detail-params">
                    <span className="voice-ai-detail-param">语速 {taskDetail?.speechRate ?? detailItem.speechRate ?? 0}</span>
                    <span className="voice-ai-detail-param">音调 {taskDetail?.pitchRate ?? detailItem.pitchRate ?? 0}</span>
                    <span className="voice-ai-detail-param">音量 {taskDetail?.loudnessRate ?? detailItem.loudnessRate ?? 0}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ===== 音色选择弹窗（公共音色 + 我的音色） ===== */}
      <div
        className={`voice-ai-modal-overlay${voiceModalOpen ? " show" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeVoiceModal();
        }}
      >
        <div className="voice-ai-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="voice-ai-modal-header">
            <div>
              <h3>音色选择</h3>
              <p>最多选择 {MAX_VOICES} 个音色，脚本中可用 @音色名 指定角色声音</p>
            </div>
            <button className="voice-ai-modal-close" onClick={closeVoiceModal}>
              <IconX />
            </button>
          </div>
          <div className="voice-ai-modal-tabs">
            {(["public", "mine"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                className={`voice-ai-modal-tab${voiceTab === tab ? " active" : ""}`}
                onClick={() => {
                  if (voiceTab === tab) return;
                  stopVoicePreviews();
                  setVoiceTab(tab);
                  setVoiceSearch("");
                }}
              >
                {tab === "public" ? "公共音色" : "我的音色"}
              </button>
            ))}
          </div>
          <div className="voice-ai-modal-search">
            <div className="voice-ai-modal-search-wrap">
              <div className="voice-ai-modal-search-icon" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
                <IconSearch />
              </div>
              <input
                type="text"
                placeholder="搜索音色名称"
                value={voiceSearch}
                onChange={(e) => setVoiceSearch(e.target.value)}
                style={{ paddingLeft: 36 }}
              />
            </div>
          </div>
<div className="voice-ai-modal-body">
            <div className="voice-ai-voice-grid">
              {mineVoicesLoading && voiceTab === "mine" ? (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "32px 0", fontSize: 13, color: "#94a3b8" }}>
                  正在加载我的音色...
                </div>
              ) : filteredVoices.length === 0 ? (
                <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "32px 0", fontSize: 13, color: "#94a3b8" }}>
                  {voiceTab === "public" ? "没有找到匹配音色" : "暂无我的音色"}
                </div>
              ) : (
                filteredVoices.map((v) => {
                  const isSelected = draftVoiceIds.includes(v.id);
                  const dotClass =
                    v.genderType === "male"
                      ? "voice-ai-voice-gender-dot blue"
                      : v.genderType === "female"
                      ? "voice-ai-voice-gender-dot pink"
                      : "";
                  const trimmedDescription = (v.description || "").trim();
                  const isPlaceholder = !trimmedDescription;
                  return (
                    <div
                      key={`${v.source}-${v.id}`}
                      className={`voice-ai-voice-card${isSelected ? " selected" : ""}`}
                      onClick={() => toggleDraftVoice(v.id)}
                    >
                      <div
                        className="voice-ai-voice-avatar"
                        style={{ background: getVoiceColor(v.id) }}
                      >
                        {v.name.charAt(0)}
                      </div>
                      <div className="voice-ai-voice-info">
                        <div className="voice-ai-voice-title">
                          <span className="voice-ai-voice-name-row">
                            <h4>{v.name}</h4>
                            {dotClass ? <span className={dotClass} /> : null}
                          </span>
                          <span className="voice-ai-voice-check">
                            <IconCheck />
                          </span>
                        </div>
                        <div
                          className={`voice-ai-voice-description${isPlaceholder ? " voice-ai-voice-description-placeholder" : ""}`}
                          title={trimmedDescription || "暂无音色描述"}
                        >
                          {trimmedDescription || "暂无音色描述"}
                        </div>
                      </div>
                      <button
                        className="voice-ai-voice-play-btn"
                        title={previewVoiceId === v.id ? "暂停" : "试听"}
                        disabled={!v.previewAudioUrl}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (previewVoiceId === v.id && previewAudioRef.current) {
                            previewAudioRef.current.pause();
                            previewAudioRef.current = null;
                            setPreviewVoiceId(null);
                          } else if (v.previewAudioUrl) {
                            stopVoicePreviews();
                            const a = new Audio(v.previewAudioUrl);
                            a.onended = () => { setPreviewVoiceId(null); previewAudioRef.current = null; };
                            a.onerror = () => { setPreviewVoiceId(null); previewAudioRef.current = null; };
                            a.play().catch(() => {});
                            previewAudioRef.current = a;
                            setPreviewVoiceId(v.id);
                          }
                        }}
                      >
                        {previewVoiceId === v.id ? <IconPause /> : <IconPlay />}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="voice-ai-modal-footer">
            <span className="voice-ai-modal-hint">
              已选择 {draftVoiceIds.length} / {MAX_VOICES}
            </span>
            <div className="voice-ai-modal-actions">
              <button className="voice-ai-modal-cancel" onClick={closeVoiceModal}>
                取消
              </button>
              <button className="voice-ai-modal-confirm" onClick={confirmVoiceSelection}>
                确认添加
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
